/**
 * SidePanel Component
 *
 * Collapsible left navigation drawer with links to Homepage, Projects,
 * Favorites, and Jobs. Expanded shows icons + labels; collapsed shows
 * icons only. Overlay mode below 768px, persistent sidebar at 768px+.
 * Persists collapsed state to localStorage.
 *
 * Requirements: 2.1, 2.2, 2.3, 2.4, 2.5, 2.6, 2.7, 2.8
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Tooltip,
    Typography,
    CircularProgress,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    Dashboard as DashboardIcon,
    Folder as FolderIcon,
    Favorite as FavoriteIcon,
    Work as WorkIcon,
    MenuBook as MenuBookIcon,
    Dns as DnsIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,

    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { LogoSection } from './LogoSection';
import { FleetStatusService, FleetStatusMetrics } from '../services/fleet-status-service';

/** Width of the sidebar when expanded */
export const SIDEBAR_EXPANDED_WIDTH = 220;
/** Width of the sidebar when collapsed (icons only) */
export const SIDEBAR_COLLAPSED_WIDTH = 60;
/** localStorage key for persisting collapsed state */
import { PROJECT_PREFIX } from '../config/project';

export const SIDEBAR_STORAGE_KEY = `${PROJECT_PREFIX}-sidebar-collapsed`;

export interface SidePanelProps {
    collapsed: boolean;
    onToggleCollapse: () => void;
    /** Mobile drawer open state (overlay mode below 768px) */
    mobileOpen?: boolean;
    /** Callback to close mobile drawer */
    onMobileClose?: () => void;
}

interface NavItem {
    label: string;
    icon: React.ReactElement;
    path: string;
}

const NAV_ITEMS: NavItem[] = [
    { label: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { label: 'Projects', icon: <FolderIcon />, path: '/projects' },
    { label: 'Recipes', icon: <MenuBookIcon />, path: '/recipes' },
    { label: 'Jobs', icon: <WorkIcon />, path: '/jobs' },
    { label: 'Favorites', icon: <FavoriteIcon />, path: '/favorites' },
];

/**
 * Helper: read collapsed state from localStorage.
 * Returns the parsed boolean, or `false` (expanded) if missing/invalid.
 */
export function readCollapsedState(): boolean {
    try {
        const raw = localStorage.getItem(SIDEBAR_STORAGE_KEY);
        if (raw === 'true') return true;
        if (raw === 'false') return false;
        return false; // default expanded
    } catch {
        return false;
    }
}

/**
 * Helper: persist collapsed state to localStorage.
 */
export function writeCollapsedState(collapsed: boolean): void {
    try {
        localStorage.setItem(SIDEBAR_STORAGE_KEY, String(collapsed));
    } catch {
        // Silently ignore storage errors
    }
}

// ─── Fleet Status Utilities (exported for testing) ──────────────────────────

export type MetricColor = 'green' | 'yellow' | 'red';
export type MetricType = 'gpu' | 'queue' | 'wait' | 'rate';

/**
 * Formats estimated wait time in seconds to a human-readable string.
 */
export function formatWaitTime(seconds: number): string {
    if (seconds <= 0) return '0 mins';
    if (seconds < 60) return '< 1 min';
    const minutes = Math.round(seconds / 60);
    return `${minutes} mins`;
}

/**
 * Returns the color indicator for a given metric value based on threshold ranges.
 */
export function getMetricColor(metricType: MetricType, value: number): MetricColor {
    switch (metricType) {
        case 'gpu':
            if (value <= 50) return 'green';
            if (value <= 80) return 'yellow';
            return 'red';
        case 'queue':
            if (value <= 1) return 'green';
            if (value <= 2) return 'yellow';
            return 'red';
        case 'wait':
            if (value <= 120) return 'green';
            if (value <= 600) return 'yellow';
            return 'red';
        case 'rate':
            if (value >= 80) return 'green';
            if (value >= 50) return 'yellow';
            return 'red';
        default:
            return 'green';
    }
}

const COLOR_MAP: Record<MetricColor, string> = {
    green: '#4caf50',
    yellow: '#ff9800',
    red: '#f44336',
};

/**
 * Compact fleet status metrics displayed inline below the nav item.
 * Auto-refreshes every 30 seconds.
 */
function FleetStatusCard() {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const [metrics, setMetrics] = useState<FleetStatusMetrics | null>(null);
    const [loading, setLoading] = useState(true);
    const serviceRef = useRef<FleetStatusService>(new FleetStatusService());
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const fetchMetrics = useCallback(async () => {
        try {
            const data = await serviceRef.current.getFleetStatus();
            setMetrics(data);
        } catch {
            // Silently fail — retain previous metrics if available
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchMetrics();
        intervalRef.current = setInterval(fetchMetrics, 30000);
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [fetchMetrics]);

    if (loading && !metrics) {
        return (
            <Box sx={{ px: 2.5, pl: 4, py: 1.5, display: 'flex', justifyContent: 'center' }} data-testid="fleet-status-card">
                <CircularProgress size={18} />
            </Box>
        );
    }

    if (!metrics) return null;

    const noInstances = (metrics.active_instance_count ?? 0) === 0;
    const noJobs = noInstances && metrics.job_completion_rate === 0;

    const stats = [
        { label: 'GPU Utilization:', value: noInstances ? '--' : (metrics.gpu_utilization_percent != null ? `${metrics.gpu_utilization_percent}%` : '-'), color: (noInstances ? 'green' : (metrics.gpu_utilization_percent != null ? getMetricColor('gpu', metrics.gpu_utilization_percent) : 'green')) as MetricColor },
        { label: 'Queue Wait Time:', value: noInstances ? '--' : (metrics.estimated_wait_seconds != null ? formatWaitTime(metrics.estimated_wait_seconds) : '-'), color: (noInstances ? 'green' : (metrics.estimated_wait_seconds != null ? getMetricColor('wait', metrics.estimated_wait_seconds) : 'green')) as MetricColor },
        { label: 'Job Success Rate:', value: noJobs ? '--' : (metrics.job_completion_rate != null ? `${metrics.job_completion_rate}%` : '-'), color: (noJobs ? 'green' : (metrics.job_completion_rate != null ? getMetricColor('rate', metrics.job_completion_rate) : 'green')) as MetricColor },
    ];

    return (
        <Box sx={{ px: 1.5, pl: 2.5, py: 0.5, overflow: 'hidden' }} data-testid="fleet-status-card">
            {stats.map((stat) => (
                <Box
                    key={stat.label}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        py: 0.3,
                        whiteSpace: 'nowrap',
                    }}
                    data-testid={`fleet-stat-${stat.label.toLowerCase().replace(/\s+/g, '-').replace(':', '')}`}
                >
                    <Box
                        sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: COLOR_MAP[stat.color],
                            flexShrink: 0,
                            boxShadow: `0 0 6px 2px ${COLOR_MAP[stat.color]}80`,
                            animation: stat.color === 'red'
                                ? 'alarm 1s ease-in-out infinite'
                                : stat.color === 'yellow'
                                    ? 'alarm 2s ease-in-out infinite'
                                    : stat.color === 'green'
                                        ? 'heartbeat 2s ease-in-out infinite'
                                        : 'none',
                            '@keyframes alarm': {
                                '0%, 100%': { transform: 'scale(1)', opacity: 1, boxShadow: `0 0 6px 2px ${COLOR_MAP[stat.color]}80` },
                                '50%': { transform: 'scale(1.4)', opacity: 0.6, boxShadow: `0 0 12px 5px ${COLOR_MAP[stat.color]}dd` },
                            },
                            '@keyframes heartbeat': {
                                '0%, 100%': { transform: 'scale(1)', boxShadow: `0 0 6px 2px ${COLOR_MAP[stat.color]}80` },
                                '14%': { transform: 'scale(1.3)', boxShadow: `0 0 8px 3px ${COLOR_MAP[stat.color]}aa` },
                                '28%': { transform: 'scale(1)', boxShadow: `0 0 6px 2px ${COLOR_MAP[stat.color]}80` },
                                '42%': { transform: 'scale(1.3)', boxShadow: `0 0 8px 3px ${COLOR_MAP[stat.color]}aa` },
                                '56%': { transform: 'scale(1)', boxShadow: `0 0 6px 2px ${COLOR_MAP[stat.color]}80` },
                            },
                        }}
                    />
                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: '0.8rem',
                            color: isDark ? 'rgba(255,255,255,0.5)' : 'text.secondary',
                            flexShrink: 0,
                        }}
                    >
                        {stat.label}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            color: COLOR_MAP[stat.color],
                        }}
                    >
                        {stat.value}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}

/**
 * Drawer content shared between persistent and overlay modes.
 */
function DrawerContent({
    collapsed,
    onToggleCollapse,
}: Pick<SidePanelProps, 'collapsed' | 'onToggleCollapse'>) {
    const location = useLocation();
    const navigate = useNavigate();
    const [fleetStatusExpanded, setFleetStatusExpanded] = useState(false);

    const currentWidth = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH;

    return (
        <Box
            sx={{
                width: currentWidth,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden',
                transition: 'width 0.2s ease',
            }}
            data-testid="sidepanel-content"
        >
            {/* Logo + collapse toggle on the same row */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: collapsed ? 'center' : 'space-between',
                    px: collapsed ? 0.5 : 2,
                    py: 1.5,
                }}
            >
                {!collapsed && <LogoSection collapsed={collapsed} />}
                <IconButton
                    onClick={onToggleCollapse}
                    size="small"
                    aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    data-testid="sidepanel-toggle"
                >
                    {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                </IconButton>
            </Box>

            {/* Navigation links */}
            <List component="nav" aria-label="main navigation">
                {NAV_ITEMS.map((item) => {
                    const isActive = location.pathname.startsWith(item.path);

                    const button = (
                        <ListItemButton
                            key={item.path}
                            selected={isActive}
                            onClick={() => navigate(item.path)}
                            data-testid={`sidepanel-nav-${item.label.toLowerCase()}`}
                            sx={{
                                minHeight: 48,
                                justifyContent: collapsed ? 'center' : 'initial',
                                px: collapsed ? 1 : 2.5,
                            }}
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: 0,
                                    mr: collapsed ? 0 : 2,
                                    justifyContent: 'center',
                                    color: isActive ? 'primary.main' : undefined,
                                }}
                            >
                                {item.icon}
                            </ListItemIcon>
                            {!collapsed && (
                                <ListItemText
                                    primary={item.label}
                                    primaryTypographyProps={{
                                        fontWeight: isActive ? 600 : 400,
                                        fontSize: '0.85rem',
                                    }}
                                />
                            )}
                        </ListItemButton>
                    );

                    // Wrap in Tooltip when collapsed so the user can still see the label
                    return collapsed ? (
                        <Tooltip key={item.path} title={item.label} placement="right">
                            {button}
                        </Tooltip>
                    ) : (
                        <React.Fragment key={item.path}>{button}</React.Fragment>
                    );
                })}

                {/* Fleet Status nav item — toggles inline metrics */}
                {(() => {
                    const fleetButton = (
                        <ListItemButton
                            onClick={() => setFleetStatusExpanded(!fleetStatusExpanded)}
                            data-testid="sidepanel-nav-fleet status"
                            sx={{
                                minHeight: 48,
                                justifyContent: collapsed ? 'center' : 'initial',
                                px: collapsed ? 1 : 2.5,
                            }}
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: 0,
                                    mr: collapsed ? 0 : 2,
                                    justifyContent: 'center',
                                }}
                            >
                                <DnsIcon />
                            </ListItemIcon>
                            {!collapsed && (
                                <>
                                    <ListItemText
                                        primary="Fleet Status"
                                        primaryTypographyProps={{ fontWeight: 400, fontSize: '0.85rem' }}
                                    />
                                    {fleetStatusExpanded
                                        ? <ExpandLessIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                        : <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                    }
                                </>
                            )}
                        </ListItemButton>
                    );

                    return collapsed ? (
                        <Tooltip key="fleet-status" title="Fleet Status" placement="right">
                            {fleetButton}
                        </Tooltip>
                    ) : (
                        <React.Fragment key="fleet-status">{fleetButton}</React.Fragment>
                    );
                })()}
            </List>

            {/* Fleet Status inline metrics — shown when expanded and not collapsed */}
            {!collapsed && fleetStatusExpanded && <FleetStatusCard />}

            {/* Spacer */}
            <Box sx={{ flexGrow: 1 }} />
        </Box>
    );
}

/**
 * SidePanel Component
 *
 * Renders a persistent sidebar at ≥768px and an overlay drawer below 768px.
 * No theme toggle — that lives in UserMenu.
 */
export function SidePanel({
    collapsed,
    onToggleCollapse,
    mobileOpen = false,
    onMobileClose,
}: SidePanelProps) {
    const theme = useTheme();
    const isWide = useMediaQuery('(min-width:768px)');

    if (isWide) {
        // Persistent sidebar
        return (
            <Drawer
                variant="permanent"
                open
                data-testid="sidepanel-drawer-persistent"
                sx={{
                    width: collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH,
                    flexShrink: 0,
                    transition: 'width 0.2s ease',
                    '& .MuiDrawer-paper': {
                        width: collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH,
                        boxSizing: 'border-box',
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        height: '100vh',
                        transition: 'width 0.2s ease',
                        borderRight: `1px solid ${theme.palette.divider}`,
                        overflowX: 'hidden',
                        overflowY: 'auto',
                    },
                }}
            >
                <DrawerContent collapsed={collapsed} onToggleCollapse={onToggleCollapse} />
            </Drawer>
        );
    }

    // Overlay drawer for narrow viewports
    return (
        <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={onMobileClose}
            data-testid="sidepanel-drawer-overlay"
            ModalProps={{ keepMounted: true }} // Better mobile perf
            sx={{
                '& .MuiDrawer-paper': {
                    width: SIDEBAR_EXPANDED_WIDTH,
                    boxSizing: 'border-box',
                },
            }}
        >
            <DrawerContent collapsed={false} onToggleCollapse={onToggleCollapse} />
        </Drawer>
    );
}
